import * as SITE_ACTIONS from 'constant/action/site'
import * as LOADER_ACTIONS from 'constant/action/loader'
import { updateArticleDescription } from 'action/article'
import { createAction } from 'redux-actions'
import buildFileMeta from 'helper/build-file-meta'
import buildImageFile from 'helper/build-image-file'

const setAllSites = createAction(SITE_ACTIONS.SET_ALL_SITES)
const setSite = createAction(SITE_ACTIONS.SET_SITE)
const setPossibleSites = createAction(SITE_ACTIONS.SET_POSSIBLE_SITES)
const updateSiteSubmitting = createAction(LOADER_ACTIONS.UPDATE_SITE_SUBMITTING)
const updateLoading = createAction(LOADER_ACTIONS.UPDATE_LOADING)
const updateSiteSearchTerm = createAction(SITE_ACTIONS.UPDATE_SITE_SEARCH_TERM)
const updateShowFooter = createAction(SITE_ACTIONS.UPDATE_SHOW_FOOTER)

export const getPossibleSites = () => async (dispatch, getState, { site }) => {
  const sites = await site.getPossibleSites()
  dispatch(setPossibleSites(sites))
}

export const getAllSites = () => async (dispatch, getState, { site }) => {
  dispatch(updateLoading(true))

  const sites = await site.getAllSites()
  dispatch(setAllSites(sites))

  dispatch(updateLoading(false))
}

export const getSite = (uuid) => async (dispatch, getState, { site }) => {
  dispatch(updateLoading(true))

  const data = await site.getSite(uuid)
  dispatch(setSite(data))

  dispatch(updateLoading(false))
}

/* eslint-disable max-lines-per-function, complexity */
export const editSite = (siteObject) => async (dispatch, getState, { site }) => {
  try {
    dispatch(updateSiteSubmitting(true))

    let editSite = {
      ...siteObject
    }

    const filesToUpload = []

    if (siteObject.site_logo instanceof File) {
      editSite.logo_meta = buildImageFile(siteObject.site_logo)

      const { file: logoFile, meta: logoMeta } = buildFileMeta(editSite.logo_meta, siteObject.site_name)

      filesToUpload.push((response) => site.uploadFile(response.logo_url, logoFile))

      editSite = {
        ...editSite,
        logo_meta: logoMeta
      }
    }

    if (siteObject.site_favicon instanceof File) {
      editSite.icon_meta = buildImageFile(siteObject.site_favicon)

      const { file: iconFile, meta: iconMeta } = buildFileMeta(editSite.icon_meta, siteObject.site_name)

      filesToUpload.push((response) => site.uploadFile(response.icon_url, iconFile))

      editSite = {
        ...editSite,
        icon_meta: iconMeta
      }
    }

    const response = await site.editSite(editSite)

    if (filesToUpload.length) {
      await Promise.all(filesToUpload.map((f) => f(response)))
    }

    dispatch(getAllSites())
    dispatch(getPossibleSites())
    dispatch(updateSiteSubmitting(false))

    return editSite
  } catch (error) {
    console.error(error)
    return { error: true, message: error.message }
  }
}

export const resetState = (uuid) => (dispatch, getState, { site }) => {
  dispatch(setSite(null))
  dispatch(updateArticleDescription(null))
}

export const searchSites = (searchTerm) => async (dispatch, getState, { site }) => {
  dispatch(updateSiteSearchTerm(searchTerm))

  if (!searchTerm) {
    return dispatch(getAllSites())
  }

  dispatch(updateLoading(true))

  const response = await site.searchSites(searchTerm)

  dispatch(setAllSites(response))
  dispatch(updateLoading(false))
}

export const addSite = (values) => async (dispatch, getState, { site }) => {
  try {
    dispatch(updateSiteSubmitting(true))
    const filesToUpload = []
    let editSite = {
      ...values
    }
    if (values.site_logo instanceof File) {
      editSite.logo_meta = buildImageFile(values.site_logo)

      const { file: logoFile, meta: logoMeta } = buildFileMeta(editSite.logo_meta, values.site_name)

      filesToUpload.push((response) => site.uploadFile(response.logo_url, logoFile))

      editSite = {
        ...editSite,
        logo_meta: logoMeta
      }
    }

    if (values.site_favicon instanceof File) {
      editSite.icon_meta = buildImageFile(values.site_favicon)

      const { file: iconFile, meta: iconMeta } = buildFileMeta(editSite.icon_meta, values.site_name)

      filesToUpload.push((response) => site.uploadFile(response.icon_url, iconFile))

      editSite = {
        ...editSite,
        icon_meta: iconMeta
      }
    }

    editSite.published = true
    const response = await site.addSite(editSite)
    if (filesToUpload.length) {
      await Promise.all(filesToUpload.map((f) => f(response)))
    }

    dispatch(getAllSites())
    dispatch(getPossibleSites())
    dispatch(updateSiteSubmitting(false))
    return editSite
  } catch (error) {
    console.error(error)
    return { error: true, message: error.message }
  }
}

export const addPermittedGroup = (siteUuid, values) => async (dispatch, getState, { site }) => {
  try {
    dispatch(updateSiteSubmitting(true))
    const response = await site.addPermittedGroup({ site_uuid: siteUuid, ...values })
    dispatch(getAllSites())
    dispatch(updateSiteSubmitting(false))
    return response
  } catch (error) {
    console.error(error)
    return { error: true, message: error.message }
  }
}

export const deletePermittedGroup = (group) => async (dispatch, getState, { site }) => {
  try {
    dispatch(updateSiteSubmitting(true))
    const response = await site.deletePermittedGroup(group)
    dispatch(getAllSites())
    dispatch(updateSiteSubmitting(false))
    return response
  } catch (error) {
    console.error(error)
    return { error: true, message: error.message }
  }
}

// postJson resolves with the Error it failed with rather than throwing, so a
// failed delete has to be turned back into one for the caller to see it.
export const deleteSite = (uuid) => async (dispatch, getState, { site }) => {
  try {
    dispatch(updateSiteSubmitting(true))
    const response = await site.deleteSite(uuid)

    if (response instanceof Error) {
      throw response
    }

    dispatch(getAllSites())
    dispatch(getPossibleSites())
    dispatch(updateSiteSubmitting(false))
    return response
  } catch (error) {
    console.error(error)
    dispatch(updateSiteSubmitting(false))
    return { error: true, message: error.message }
  }
}

export const editPermittedGroup = (values) => async (dispatch, getState, { site }) => {
  try {
    dispatch(updateSiteSubmitting(true))
    const response = await site.editPermittedGroup(values)
    dispatch(getAllSites())
    dispatch(updateSiteSubmitting(false))
    return response
  } catch (error) {
    console.error(error)
    return { error: true, message: error.message }
  }
}

export const updateFooterState = (webpage) => (dispatch, getState, { site }) => {
  try {
    dispatch(updateShowFooter(!webpage))
  } catch (error) {
    console.error(error)
  }
}
